import { ActionReference, GameObjectReference, GameState } from "@shared/types";
import { LitElement, TemplateResult, css, html, nothing } from "lit";
import { customElement, property } from "lit/decorators.js";
import { getState, performAction } from "../services/routeService";
import { gsap } from "gsap";
import { CustomEase } from "gsap/CustomEase";
import { RoughEase, ExpoScaleEase, SlowMo } from "gsap/EasePack";
import { Flip } from "gsap/Flip";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Observer } from "gsap/Observer";
import { ScrollToPlugin } from "gsap/ScrollToPlugin";
import { Draggable } from "gsap/Draggable";
import { MotionPathPlugin } from "gsap/MotionPathPlugin";
import { EaselPlugin } from "gsap/EaselPlugin";
import { PixiPlugin } from "gsap/PixiPlugin";
import { TextPlugin } from "gsap/TextPlugin";
import { ifDefined } from "lit/directives/if-defined.js";

import "boxicons";

gsap.registerPlugin(
    Flip,
    ScrollTrigger,
    Observer,
    ScrollToPlugin,
    Draggable,
    MotionPathPlugin,
    EaselPlugin,
    PixiPlugin,
    TextPlugin,
    RoughEase,
    ExpoScaleEase,
    SlowMo,
    CustomEase
);

@customElement("game-canvas")
export class GameCanvas extends LitElement {
    @property({ type: String }) public theme = localStorage.getItem("theme") || "light-mode";
    @property({ type: String }) public currentAudio: string | undefined;
    public static styles = css`
        .mode-button {
            margin-right: 10px;
            font-size: 16px;
            padding: 8px 12px;
            border-radius: 5px;
            cursor: pointer;
            border: 2px solid transparent;
            transition: background-color 0.3s, color 0.3s;
        }
        .box-icon {
            color: white;
            padding: 3px;
            cursor: pointer;
            width: 60px;
            position: absolute;
            left: 50%;
            transform: translateX(-50%);
        }
        h3 {
            text-align: center;
            color: black;
        }

        .footer .button.disabled {
            opacity: 0.5;
            cursor: not-allowed;
            pointer-events: none;
        }
        :host([theme="light-mode"]) {
            --background-color: #f0f0f0;
            --text-color: #000000;
            --border-color: #747171;
        }

        :host([theme="dark-mode"]) {
            --background-color: #000000;
            --text-color: #ffffff;
            --border-color: #000000;
        }

        .game {
            background-color: var(--background-color);
            color: var(--text-color);
            display: flex;
            grid-template-columns: 1fr;
            grid-template-rows: auto auto 1fr auto;
            grid-column-gap: 0px;
            grid-row-gap: 0px;
            flex-direction: column;
            justify-content: space-between;
            max-width: 100%;
            height: 100vh;
        }
        .header,
        .footer {
            padding-left: 5%;
            padding-right: 5%;
            text-align: center;
        }
        .title {
            text-align: center;
            height: 20px;
            width: auto;
            opacity: 0;
        }

        .header {
            display: flex;
            height: auto;
            width: auto;
            overflow: wrap;
            flex-direction: column;
            justify-content: center;
            margin-top: 1em;
        }

        .header img {
            max-width: calc(80% - 15%);
            max-height: 80%;
            width: auto;
            height: auto;
            display: block;
            margin: 8px auto;
        }

        .content {
            background-color: var(--background-color);
            color: var(--text-color);
            flex-grow: 1;
            margin-top: 2px;
            overflow: wrap;
            display: inline-block;
            border-radius: 5px;
        }

        .content p {
            margin: 0 0 1px 0;
            overflow: wrap;
            padding-left: 20px;
        }

        .footer {
            border-radius: 10px 10px 0 0;
            background: linear-gradient(to bottom, #64b779 0%, #0b3258 100%);
            border: 2px solid #64b779;
            display: flex;
            height: auto;
            width: auto;
            overflow: wrap;
            flex-direction: column;
        }

        .footer .buttons {
            display: flex;
            flex-direction: column;
            overflow: auto;
            padding: 10px 10px 0 10px;
        }

        .footer .button {
            background-color: var(--background-color);
            color: var(--text-color);
            padding: 5px 10px;
            margin: 0 0 10px 10px;
            text-transform: uppercase;
            cursor: pointer;
            display: inline-block;
            user-select: none;
            border: 3px solid black;
            box-shadow: inset 0 0 20px rgba(255, 255, 255, 0);
            outline: 1px solid;
            outline-color: rgba(255, 255, 255, 0.5);
            outline-offset: 0px;
            text-shadow: none;
            transition: all 1250ms cubic-bezier(0.19, 1, 0.22, 1);
        }

        .footer .button:hover {
            border: 3px solid;
            box-shadow: inset 0 0 20px rgba(255, 255, 255, 0.5), 0 0 20px rgba(255, 255, 255, 0.2);
            outline-color: rgba(255, 255, 255, 0);
            outline-offset: 15px;
            text-shadow: 1px 1px 2px #427388;
        }

        .sidebar {
            position: fixed;
            height: 100%;
            width: 16%;
            left: 0;
            top: 0;
            background: linear-gradient(to bottom, #64b779 0%, #0b3258 90%);
            padding: 4px;
            word-wrap: break-word;
            overflow-wrap: break-word;
            white-space: normal;
        }
        .right-sidebar {
            position: fixed;
            height: 100%;
            width: 16%;
            right: 0;
            top: 0;
            background: linear-gradient(to bottom, #64b779 0%, #0b3258 90%);
            padding: 4px;
            word-wrap: break-word;
            overflow-wrap: break-word;
            white-space: normal;
        }
        .gif {
            position: absolute;
            bottom: 0;
            left: 0;
            width: 95%;
            padding-left: 6px;
        }
        .gif img {
            max-width: 100%;
            max-height: 100%;
            width: auto;
            height: auto;
            display: block;
            margin: 0 auto;
        }

        .logo img {
            max-width: 70%;
            height: auto;
            display: block;
            margin: 10px auto;
        }
        .pilar{
            position: absolute;
            bottom: 0;
            left: 0;
            width: auto;
            text-align: center;
            margin: 10px auto;
        }
        .pilar img {
            max-height: 100%;
            max-width: 100%;
            height: 100%;
            width: 100%;
            margin-left:16px;
        }

        .map img {
            max-width: 100%;
            max-height: 100%;
            width: auto;
            height: auto;
            display: block;
            margin: 0 auto;
            border: 4px solid #000000;
        }

        .map {
            position: relative;
            padding-right: 8px;
        }

        .pointer {
            position: absolute;
            display: block;
            top: 0;
            left: 0;
            width: 20px;
            height: 35px;
            transform: translate(-50%, -50%) scale(1);
        }

        @media (max-width: 1300px) {
            .sidebar,
            .right-sidebar {
                width: 20%;
            }

            .header,
            .footer,
            .content {
                padding-left: 20%;
                padding-right: 20%;
            }
            .title {
                padding-left: 10%;
                padding-right: 10%;
            }

            .pointer {
                display: none;
            }
        }

        @media (max-width: 998px) {
            .sidebar {
                width: 30%;
            }
            .right-sidebar {
                display: none;
            }

            .header,
            .footer,
            .content {
                padding-left: 30%;
                padding-right: 10px;
            }
            .title {
                padding-left: 20%px;
                padding-right: 10px;
            }

            .pointer {
                display: none;
            }
        }
        @media only screen and (max-width: 768px) {
            .right-sidebar {
                display: none;
            }
            .sidebar{
                width:20%;
            }

            .header,
            .footer,
            .content {
                padding-left: 20%;
                padding-right: 10px;
            }

            .footer .buttons {
                flex-direction: row;
                flex-wrap: wrap;
            }

            .footer .button {
                flex: 1;
                margin: 5px;
            }
        }
        @media only screen and (max-width: 480px) {
            .header,
            .footer,
            .content {
                padding-left: 20%;
                padding-right: 5px;
            }
            h3{
                display:none;
            }

            .footer .button {
                padding: 10px;
            }
        }
    `;
    public constructor() {
        super();
        this.applyTheme();
    }
    private pointerInitialPosition = { x: 0, y: 0 };
    private roomTitle?: string;
    private roomImages?: string[];
    private mapImages?: string[];
    private rightImages?: string[];
    public contentText?: string[] | undefined;
    private actionButtons?: ActionReference[];
    private gameObjectButtons?: GameObjectReference[];

    private selectedActionButton?: ActionReference;
    private selectedGameObjectButtons: Set<GameObjectReference> = new Set<GameObjectReference>();

    private displayedText: string[] = [];
    private isTextDisplaying = false;
    private gameState: GameState | undefined;

    public connectedCallback(): void {
        super.connectedCallback();

        void this.refreshState();
    }

    private async refreshState(): Promise<void> {
        try {
            const state: GameState = await getState();
            if (state) {
                this.updateState(state);
            } else {
                console.error("Received null or undefined state from getState().");
            }
        } catch (error) {
            console.error("Error fetching game state:", error);
        }
    }
    private applyTheme(): void {
        this.setAttribute("theme", this.theme);
    }

    private setLightMode(): void {
        this.theme = "light-mode";
        localStorage.setItem("theme", this.theme);
        this.applyTheme();
    }

    private setDarkMode(): void {
        this.theme = "dark-mode";
        localStorage.setItem("theme", this.theme);
        this.applyTheme();
    }

    private updateAudio(newAudio: string | undefined): void {
        this.currentAudio = newAudio;
        const audioElement: HTMLAudioElement | null | undefined =
            this.shadowRoot?.querySelector("#background-audio");
        if (audioElement) {
            if (newAudio) {
                audioElement.src = newAudio;
                audioElement.play().catch((error) => console.error("Error playing audio:", error));
            } else {
                audioElement.pause();
                audioElement.src = "";
            }
        }
    }
    private updateState(state: GameState): void {
        if (this.gameState?.pointerPosition && state.pointerPosition) {
            this.animatePointerToNewPosition(state.pointerPosition);
        }
        const newAudio: any = state.roomAudio;
        if (newAudio !== this.currentAudio) {
            this.updateAudio(newAudio);
        }
        //Reset the component
        this.gameState = state;
        this.requestUpdate();
        this.roomTitle = state.roomTitle;
        this.roomImages = state.roomImages;
        this.mapImages = state.mapImages;
        this.rightImages = state.rightImages;
        this.contentText = state.text;
        this.actionButtons = state.actions;
        this.gameObjectButtons = state.objects;

        this.selectedActionButton = undefined;
        this.selectedGameObjectButtons.clear();

        this.requestUpdate();

        this.displayedText = this.contentText.map(() => "");
        this.contentText.forEach((text, index) => {
            if (!this.isTextDisplaying) {
                this.displayText(text, index);
            }
        });

        this.animateTitle();
    }

    private animatePointerToNewPosition(newPosition: { x: number; y: number }): void {
        const pointerElement: Element | null | undefined = this.shadowRoot?.querySelector(".pointer");
        if (pointerElement) {
            gsap.fromTo(
                pointerElement,
                {
                    x: this.pointerInitialPosition.x,
                    y: this.pointerInitialPosition.y,
                },
                {
                    x: newPosition.x,
                    y: newPosition.y,
                    duration: 2,
                    ease: "power2.out",
                    onComplete: () => {
                        this.pointerInitialPosition = { ...newPosition };
                    },
                }
            );
        }
    }
    public displayText(text: string, paragraphIndex: number): void {
        if (this.isTextDisplaying || !this.contentText) {
            return;
        }

        this.isTextDisplaying = true;
        this.requestUpdate();
        const display: any = (index: number) => {
            if (index < text.length) {
                this.displayedText[paragraphIndex] += text[index];
                this.requestUpdate();
                setTimeout(() => display(index + 1), 5);
            } else {
                this.isTextDisplaying = false;
                this.requestUpdate();
                if (paragraphIndex < (this.contentText?.length ?? 0) - 1) {
                    this.displayText(this.contentText![paragraphIndex + 1], paragraphIndex + 1);
                }
            }
        };

        display(0);
    }

    private async handleClickAction(button: ActionReference): Promise<void> {
        if (this.isTextDisplaying) {
            return;
        }

        if (button.needsObject) {
            this.selectedActionButton = button;
            this.selectedGameObjectButtons.clear();

            this.requestUpdate();
        } else {
            const state: any = await performAction(button.alias);

            if (state === undefined) {
                return;
            }

            this.updateState(state);
        }
    }

    public updated(changedProperties: Map<PropertyKey, unknown>): void {
        super.updated(changedProperties);
        if (changedProperties.has("roomTitle")) {
            this.animateTitle();
        }
        if (changedProperties.has("gameState")) {
            console.log("Game state updated:", this.gameState);
            if (this.gameState && this.gameState.pointerPosition) {
                this.updatePointerPosition(this.gameState.pointerPosition);
            } else {
                console.error("GameState or pointerPosition is undefined.");
            }
        }
    }
    private updatePointerPosition(newPosition: { x: number; y: number }): void {
        console.log("Pointer will move to:", newPosition);
        this.animatePointerToNewPosition(newPosition);
    }
    private async handleClickObject(button: GameObjectReference): Promise<void> {
        if (!this.selectedActionButton) {
            return;
        }

        this.selectedGameObjectButtons.add(button);

        const state: GameState | undefined = await performAction(
            this.selectedActionButton.alias,
            [...this.selectedGameObjectButtons].map((e) => e.alias)
        );

        if (this.selectedGameObjectButtons.size >= 2) {
            this.selectedActionButton = undefined;
            this.selectedGameObjectButtons.clear();
        }

        this.requestUpdate();

        if (state === undefined) {
            return;
        }

        this.updateState(state);
    }
    private animateTitle(): void {
        const titleElement: HTMLElement | null | undefined = this.shadowRoot?.querySelector("#title");
        if (titleElement) {
            gsap.set(titleElement, { opacity: 0 });

            // Animate the title to fade in
            gsap.to(titleElement, {
                duration: 1, // Duration of 1 second for the fade-in effect
                opacity: 1, // Animate to full opacity
                ease: "power2.inOut", // Smooth easing for the animation
            });
        }
    }

    protected render(): TemplateResult {
        const pointerPosition: { x: number; y: number } = this.gameState?.pointerPosition || { x: 0, y: 0 };
        return html`
            <div class="game">
                <audio id="background-audio" loop autoplay>
                    <source src="${ifDefined(this.currentAudio)}" type="audio/mpeg" />
                    Your browser does not support the audio element.
                </audio>
                <div class="sidebar">
                    <div class="map">
                        ${this.mapImages?.map((url) => html`<img src="/assets/img/rooms/${url}.png" />`)}
                        <svg
                            class="pointer"
                            style="transform: translate(${pointerPosition.x}px, ${pointerPosition.y}px);"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 120 166"
                        >
                            <path
                                d="M48.4399 98.3639L45 97.854V101.331V148.462V149.546L45.6931 150.38L53.9507 160.312L53.951 160.312C55.5348 162.216 57.8961 163 60.0019
                 163C62.1076 163 64.4689 162.216 66.0527 
                160.312L66.0535 160.311L74.3073 150.379L75 149.546V148.462V101.331V97.854L71.5601 98.3639C67.7881 98.923 63.9351 99.2519 60 99.2519C56.0649 
                99.2519 52.2119 98.923 48.4399 98.3639ZM60 3C29.1707 3 3 23.1512 3 49.3133C3 75.4755 29.1707 95.6267 60 95.6267C90.8293 95.6267 117 75.4755
                 117 49.3133C117 23.1512 90.8293 3 60 3ZM60 25.8598C44.9367 25.8598 31.5085 35.787 31.5 49.3005C31.4886 49.3316 31.4455 49.411 31.3039 49.5246C31.056 
                 49.7233 30.6042 49.9228 30 49.9228C29.3958 49.9228 28.944 49.7233 28.6961 49.5246C28.5545 49.411 28.5114 49.3316 28.5 49.3006C28.5088 36.2875 41.9912
                  24.6409 60 24.6409C60.6042 24.6409 61.056 24.8404 61.3039 25.0392C61.429 25.1395 61.4772 25.2132 61.4946 25.2504C61.4772 25.2876 61.429 25.3612 61.3039 
                  25.4616C61.056 25.6604 60.6042 25.8598 60 25.8598ZM31.5034 49.2861C31.5039 49.2862 31.5035 49.2905 31.5006 49.2988C31.5015 49.2901 31.5029 49.286 31.5034 
                  49.2861ZM28.4966 49.2861C28.4971 49.286 28.4985 49.2901 28.4994 49.2988C28.4965 49.2905 28.4961 49.2862 28.4966 49.2861ZM61.5035 25.2776C61.503 25.2779 61.5015
                   25.2738 61.5006 25.265C61.5035 25.273 61.5039 25.2774 61.5035 25.2776ZM61.5006 25.2358C61.5015 25.2271 61.5029 25.223 61.5034 25.2231C61.5039 25.2232 61.5035 25.2276 61.5006 25.2358Z"
                                fill="darkblue"
                                stroke="white"
                                stroke-width="5"
                            />
                        </svg>
                    </div>
                    <h3>Theme:</h3>

                    <div class="box-icon">
                        ${this.theme === "dark-mode"
                            ? html`<box-icon name="sun" @click="${this.setLightMode}"></box-icon>`
                            : html`<box-icon
                                  name="sun"
                                  type="solid"
                                  @click="${this.setLightMode}"
                              ></box-icon>`}

                        <!-- Dark Mode Icon -->
                        ${this.theme === "light-mode"
                            ? html`<box-icon name="moon" @click="${this.setDarkMode}"></box-icon>`
                            : html`<box-icon
                                  name="moon"
                                  type="solid"
                                  @click="${this.setDarkMode}"
                              ></box-icon>`}
                    </div>
                    <div class="gif">
                        ${this.rightImages?.map((url) => html`<img src="/assets/img/rooms/${url}.png" />`)}
                    </div>
                </div>
                ${this.renderTitle()} ${this.renderHeader()} ${this.renderContent()} ${this.renderFooter()}
            </div>
            <div class="right-sidebar">
                <div class="logo">
                    ${this.roomImages?.map(() => html`<img src="/assets/img/rooms/logo.png" />`)}
                </div>
                <div class="pilar">
                    ${this.roomImages?.map(() => html`<img src="/assets/img/rooms/pilar2.png" />`)}
                </div>
            </div>
        `;
    }
    private renderTitle(): TemplateResult {
        if (this.roomTitle) {
            return html`<div class="title" id="title">${this.roomTitle}</div>`;
        }
        return html`${nothing}`;
    }

    private renderHeader(): TemplateResult {
        if (this.roomImages && this.roomImages.length > 0) {
            return html`
                <div class="header">
                    ${this.roomImages?.map((url) => html`<img src="/assets/img/rooms/${url}.png" />`)}
                </div>
            `;
        }

        return html`${nothing}`;
    }

    public renderContent(): TemplateResult {
        return html`<div class="content">${this.displayedText.map((text) => html`<p>${text}</p>`)}</div>`;
    }

    private renderFooter(): TemplateResult {
        return html`
            <div class="footer">
                <div class="buttons">
                    <div>
                        ${this.actionButtons?.map(
                            (button) => html`<a
                                class="button ${this.selectedActionButton === button ? "active" : ""} ${this
                                    .isTextDisplaying
                                    ? "disabled"
                                    : ""}"
                                @click=${(): void => void this.handleClickAction(button)}
                                >${button.label}</a
                            >`
                        )}
                    </div>
                    <div>
                        ${this.selectedActionButton
                            ? this.gameObjectButtons?.map(
                                  (button) => html`<a
                                      class="button ${this.selectedGameObjectButtons.has(button)
                                          ? "active"
                                          : ""} ${this.isTextDisplaying ? "disabled" : ""}"
                                      @click=${(): void => void this.handleClickObject(button)}
                                      >${button.name}</a
                                  >`
                              )
                            : nothing}
                    </div>
                </div>
            </div>
        `;
    }
}
